import { createAsyncThunk } from '@reduxjs/toolkit'

import { socket } from '@/socket'
import { API } from '@/api'

import { ThunkConfig } from '@/stores/slices/Labeling/types'
import { LabelingObject } from '@/api/LabelingNode/types'

type CreateCommentPayload = {
  labelingItemId: string
  region: { x: number, y: number, coordinateSpace?: 'video-normalized' }
  comment: string
  metadata?: any
}

type AppendCommentPayload = {
  labelingObjectId: string
  comment: string
}

type DeleteCommentPayload = {
  labelingObjectId: string
  commentId: string
}

type ToggleConfirmCommentPayload = {
  labelingObjectId: string
}

type ConfirmAllCommentsPayload = {
  labelingItemId?: string
  shouldCompleteReview: boolean
}

type ConfirmAllCommentsRes = {
  data: LabelingObject[]
  labelingItemId: string
  shouldCompleteReview: boolean
}

export const createComment = createAsyncThunk<LabelingObject, CreateCommentPayload, ThunkConfig>(
  'labeling/create-comment',
  async (payload, thunkAPI) => {
    const { labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const res = await API().LabelingReview.createComment(payload)

    socket?.Labeling.emitCreateReviewObject({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      res,
    } as any)
    return res
  }
)

export const appendComment = createAsyncThunk<LabelingObject, AppendCommentPayload, ThunkConfig>(
  'labeling/append-comment',
  async (payload, thunkAPI) => {
    const { labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const res = await API().LabelingReview.appendComment(payload)

    socket?.Labeling.emitAddComment({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      res,
    } as any)
    return res
  }
)

export const deleteComment = createAsyncThunk<LabelingObject, DeleteCommentPayload, ThunkConfig>(
  'labeling/delete-sub-comment',
  async (payload, thunkAPI) => {
    const { labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const { labelingObjectId, commentId } = payload

    const res = await API().LabelingReview.removeComment(labelingObjectId, commentId)

    socket?.Labeling.emitDeleteComment({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      res,
    } as any)
    return res
  }
)

export const toggleConfirmComment = createAsyncThunk<LabelingObject, ToggleConfirmCommentPayload, ThunkConfig>(
  'labeling/toggle-confirm-comment',
  async (payload, thunkAPI) => {
    const { labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const { labelingObjectId } = payload

    const res = await API().LabelingReview.toggleResolveComment(labelingObjectId)

    socket?.Labeling.emitResolveComment({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      res,
    })
    return res
  }
)

export const confirmAllComments = createAsyncThunk<ConfirmAllCommentsRes, ConfirmAllCommentsPayload, ThunkConfig>(
  'labeling/confirm-all-comments',
  async (payload, thunkAPI) => {
    const { labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const { labelingItemId, shouldCompleteReview } = payload

    const res = await API().LabelingReview.confirmReviewObjects(labelingNodeId, { shouldCompleteReview, labelingItemId })

    socket?.Labeling.emitConfirmAllComments({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      shouldCompleteReview,
      labelingItemId,
    })
    return {
      data: res.data,
      labelingItemId,
      shouldCompleteReview,
    } as ConfirmAllCommentsRes
  }
)
